import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import FileUpload from './FileUpload';

const StepTwo = ({ formData = {}, onUpdate, onBack, onComplete }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const textareaClassName =
    'w-full px-4 py-3 border-2 border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary/20 transition-smooth bg-background text-foreground font-body text-sm resize-none';

  const handleChange = (e) => {
    const { name, value } = e.target;
    onUpdate({ [name]: value });
  };

  const validateMinLengthField = (value, emptyMessage, shortMessage) => {
    if (!value?.trim()) return emptyMessage;
    if (value.trim().length < 20) return shortMessage;
    return '';
  };

  const validateForm = () => {
    const newErrors = {};

    const fieldValidations = [
      {
        key: 'problemSolved',
        empty: 'Please describe the problem your business solves',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'biggestChallenge',
        empty: 'Please describe your biggest challenge',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'keyPartners',
        empty: 'Please enter your key partners',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'valueProposition',
        empty: 'Please enter your value proposition',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'customerRelationships',
        empty: 'Please describe your customer relationships',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'customerSegments',
        empty: 'Please enter your customer segments',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'keyActivities',
        empty: 'Please enter your key activities',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'channels',
        empty: 'Please enter your channels',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'keyResources',
        empty: 'Please enter your key resources',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'costStructure',
        empty: 'Please enter your cost structure',
        short: 'Please provide at least 20 characters',
      },
      {
        key: 'revenueStreams',
        empty: 'Please enter your revenue streams',
        short: 'Please provide at least 20 characters',
      },
    ];

    fieldValidations.forEach(({ key, empty, short }) => {
      const error = validateMinLengthField(formData?.[key], empty, short);
      if (error) {
        newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        fullName: formData?.fullName || '',
        mobileNumber: formData?.mobileNumber || '',
        email: formData?.email || '',
        companyName: formData?.companyName || '',
        website: formData?.website || '',
        industry: formData?.industry || '',
        businessType: formData?.businessType || '',
        revenueRange: formData?.revenueRange || '',
        primaryGoal: formData?.primaryGoal || '',
        problemSolved: formData?.problemSolved || '',
        biggestChallenge: formData?.biggestChallenge || '',
        keyPartners: formData?.keyPartners || '',
        valueProposition: formData?.valueProposition || '',
        customerRelationships: formData?.customerRelationships || '',
        customerSegments: formData?.customerSegments || '',
        keyActivities: formData?.keyActivities || '',
        channels: formData?.channels || '',
        keyResources: formData?.keyResources || '',
        costStructure: formData?.costStructure || '',
        revenueStreams: formData?.revenueStreams || '',
      };

      console.log('Submitting payload:', payload);

      const response = await fetch('http://localhost:5000/api/business/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      console.log('Save response status:', response.status);
      console.log('Save response data:', result);

      if (!response.ok) {
        throw new Error(result?.message || 'Failed to save business data');
      }

      onComplete(result?.data);
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.message || 'Failed to complete setup');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (file) => {
    onUpdate({ financialFile: file });
  };

  const renderTextareaField = ({
    label,
    name,
    placeholder,
    rows = 4,
    required = true,
    helperText = 'Minimum 20 characters required',
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-foreground font-body">
        {label} {required && <span className="text-destructive">*</span>}
      </label>

      <textarea
        name={name}
        value={formData?.[name] || ''}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        className={textareaClassName}
        required={required}
      />

      {errors?.[name] && (
        <p className="text-sm text-destructive font-caption mt-1">
          {errors[name]}
        </p>
      )}

      <p className="text-xs text-muted-foreground font-caption">
        {helperText}
      </p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {renderTextareaField({
        label: 'What problem does your business solve?',
        name: 'problemSolved',
        placeholder:
          'Describe the core problem your business addresses and how you solve it...',
        rows: 5,
      })}

      {renderTextareaField({
        label: 'What is your biggest challenge right now?',
        name: 'biggestChallenge',
        placeholder:
          "Share the main obstacle or challenge you're currently facing in your business...",
        rows: 5,
      })}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground font-body">
          Upload Financial Data (Optional)
        </label>
        <FileUpload onFileSelect={handleFileSelect} />
        <p className="text-xs text-muted-foreground font-caption">
          Upload your financial Excel file to get more accurate predictions and insights
        </p>
      </div>

      <div className="pt-2">
        <h3 className="text-lg font-semibold text-foreground font-heading mb-1">
          Business Model Canvas Details
        </h3>
        <p className="text-sm text-muted-foreground font-body mb-4">
          Fill in the 9 business model canvas sections below
        </p>
      </div>

      {renderTextareaField({
        label: 'Key Partners',
        name: 'keyPartners',
        placeholder: 'Who are your key partners and suppliers?',
      })}

      {renderTextareaField({
        label: 'Value Proposition',
        name: 'valueProposition',
        placeholder: 'What unique value do you deliver to customers?',
      })}

      {renderTextareaField({
        label: 'Customer Relationships',
        name: 'customerRelationships',
        placeholder: 'How do you acquire, retain, and grow customers?',
      })}

      {renderTextareaField({
        label: 'Customer Segments',
        name: 'customerSegments',
        placeholder: 'Who are your target customers?',
      })}

      {renderTextareaField({
        label: 'Key Activities',
        name: 'keyActivities',
        placeholder: 'What important activities does your business perform?',
      })}

      {renderTextareaField({
        label: 'Channels',
        name: 'channels',
        placeholder: 'Through which channels do you reach customers?',
      })}

      {renderTextareaField({
        label: 'Key Resources',
        name: 'keyResources',
        placeholder: 'What resources are essential for your business?',
      })}

      {renderTextareaField({
        label: 'Cost Structure',
        name: 'costStructure',
        placeholder: 'What are the major costs in your business model?',
      })}

      {renderTextareaField({
        label: 'Revenue Streams',
        name: 'revenueStreams',
        placeholder: 'How does your business generate revenue?',
      })}

      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={onBack}
          iconName="ArrowLeft"
          iconPosition="left"
          fullWidth
          disabled={isSubmitting}
        >
          Back to Step 1
        </Button>

        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isSubmitting}
          disabled={isSubmitting}
          iconName="Check"
          iconPosition="left"
          fullWidth
        >
          {isSubmitting ? 'Creating Account...' : 'Complete Setup'}
        </Button>
      </div>
    </form>
  );
};

export default StepTwo;